/**
 * Column Customizer Component
 *
 * Dropdown UI for customizing table columns:
 * - Toggle column visibility with checkboxes
 * - Drag-and-drop column reordering
 * - Show/Hide All quick actions
 * - Reset to default configuration
 * - Mobile-responsive design
 * - Persists preferences to localStorage
 */

import React, { useState, useRef, useEffect } from 'react';
import { Settings, Eye, EyeOff, RotateCcw, GripVertical } from 'lucide-react';
import { useTableColumnStore } from '@shared/store/tableColumnStore';
import { Checkbox } from './Checkbox';
import type { TableType, ColumnConfig } from '@shared/types/table-columns';

export interface ColumnCustomizerProps {
  /** Table type (campaigns or organizations) */
  tableType: TableType;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Column Customizer - Dropdown menu for managing table columns
 *
 * Features:
 * - Drag-and-drop reordering with grip handles
 * - Checkbox list for each column (respects canHide flag)
 * - Show All / Hide All quick actions
 * - Reset to Defaults button
 * - Badge showing count of hidden columns
 * - Click-outside to close
 * - Keyboard navigation (Escape to close)
 * - State persisted to localStorage
 *
 * @example
 * ```tsx
 * <ColumnCustomizer tableType="campaigns" />
 * ```
 */
export const ColumnCustomizer: React.FC<ColumnCustomizerProps> = ({
  tableType,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Zustand store actions
  const columns = useTableColumnStore((state) => state[tableType]);
  const toggleColumn = useTableColumnStore((state) => state.toggleColumn);
  const showAllColumns = useTableColumnStore((state) => state.showAllColumns);
  const hideAllColumns = useTableColumnStore((state) => state.hideAllColumns);
  const resetToDefaults = useTableColumnStore((state) => state.resetToDefaults);
  const reorderColumns = useTableColumnStore((state) => state.reorderColumns);
  const getHiddenCount = useTableColumnStore((state) => state.getHiddenCount);

  const hiddenCount = getHiddenCount(tableType);

  // Get column list sorted by order
  const columnList = (Object.values(columns) as ColumnConfig[]).sort((a, b) => a.order - b.order);

  // Filter to only columns that can be hidden (for the list)
  const customizableColumns = columnList.filter((col) => col.canHide);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleColumnToggle = (columnId: string) => {
    toggleColumn(tableType, columnId);
  };

  const handleShowAll = () => {
    showAllColumns(tableType);
  };

  const handleHideAll = () => {
    hideAllColumns(tableType);
  };

  const handleReset = () => {
    resetToDefaults(tableType);
    setIsOpen(false);
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      // Create new order array based on all columns (not just customizable)
      const newOrder = [...columnList];
      const [draggedColumn] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dragOverIndex, 0, draggedColumn);

      // Update the store with new order
      reorderColumns(tableType, newOrder.map((col) => col.id));
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={handleToggle}
        className="relative flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-surface/50 active:bg-dark-active lg:hover:bg-dark-surface/50 transition-all duration-150 text-dark-text"
        aria-label="Customize columns"
        aria-expanded={isOpen}
        data-testid='unknown-toggle-btn'
        data-ux='toggle'
        data-interaction-id='unknown-toggle-btn'>
        <Settings size={18} />
        <span className="hidden md:inline text-sm">Columns</span>
        {hiddenCount > 0 && (
          <span className="absolute -top-1 -right-1 md:static md:ml-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-brand-500 text-white text-xs font-semibold rounded-full">
            {hiddenCount}
          </span>
        )}
      </button>
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-dark-surface border border-dark-border/30 rounded-xl shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-dark-border/30">
            <h3 className="text-sm font-semibold text-white">Customize Columns</h3>
            <p className="text-xs text-dark-muted mt-1">
              Show or hide table columns
            </p>
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-2 border-b border-dark-border/30 flex gap-2">
            <button
              onClick={handleShowAll}
              className="flex items-center gap-1.5 px-2 py-1 text-xs bg-dark-surface/50 hover:bg-dark-active active:bg-dark-surface/60 lg:hover:bg-dark-active rounded transition-all duration-150 text-dark-text"
              title="Show all columns"
              data-testid='unknown-show-all-btn'
              data-ux='show-all'
              data-interaction-id='unknown-show-all-btn'>
              <Eye size={14} />
              Show All
            </button>
            <button
              onClick={handleHideAll}
              className="flex items-center gap-1.5 px-2 py-1 text-xs bg-dark-surface/50 hover:bg-dark-active active:bg-dark-surface/60 lg:hover:bg-dark-active rounded transition-all duration-150 text-dark-text"
              title="Hide all columns (except required)"
              data-testid='unknown-hide-all-btn'
              data-ux='hide-all'
              data-interaction-id='unknown-hide-all-btn'>
              <EyeOff size={14} />
              Hide All
            </button>
            <button
              onClick={handleReset}
              className="ml-auto flex items-center gap-1.5 px-2 py-1 text-xs bg-dark-surface/50 hover:bg-dark-active active:bg-dark-surface/60 lg:hover:bg-dark-active rounded transition-all duration-150 text-dark-text"
              title="Reset to default columns"
              data-testid='unknown-reset-btn'
              data-ux='reset'
              data-interaction-id='unknown-reset-btn'>
              <RotateCcw size={14} />
              Reset
            </button>
          </div>

          {/* Column List */}
          <div className="max-h-80 overflow-y-auto">
            {customizableColumns.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-dark-muted">
                No customizable columns
              </div>
            ) : (
              <div className="py-2">
                {customizableColumns.map((column, index) => (
                  <div
                    key={column.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragLeave={handleDragLeave}
                    className={`flex items-center gap-2 px-4 py-2 hover:bg-dark-surface/50 active:bg-dark-active lg:hover:bg-dark-surface/50 cursor-move transition-all duration-150 ${
                      dragOverIndex === index && draggedIndex !== index
                        ? 'border-t-2 border-brand-500'
                        : ''
                    } ${draggedIndex === index ? 'opacity-50' : ''}`}
                  >
                    <GripVertical size={14} className="text-dark-muted flex-shrink-0" />
                    <label
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={column.visible}
                        onChange={() => handleColumnToggle(column.id)}
                        size={16}
                        data-testid='unknown-field-input'
                        data-ux='field-edit' />
                      <span className="text-sm text-dark-text flex-1">{column.label}</span>
                      {!column.visible && (
                        <span className="text-xs text-dark-muted">Hidden</span>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Info */}
          <div className="px-4 py-2 border-t border-dark-border/30 bg-dark-surface/50">
            <p className="text-xs text-dark-muted text-center">
              {columnList.filter((col) => col.visible).length} of {columnList.length} columns visible
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
